import React, {useState, useCallback, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import Modal from '../../../../accountManagement/shared/Modal'
import {EmailLookup} from '../../../../accountManagement/features/EmailLookup'
import {FooterRow} from '../../../../accountManagement/features/AddAccountModal/AddAccountModal.styles'
import {Button} from '../../../../accountManagement/shared/Button'
import {ProposalAccountInformation} from './account-information'
import {useDispatch, useSelector} from 'react-redux'
import {
  resetProposalInfo,
  resetRecurlyAccountInfo,
  updateProposalInfo,
  selectProposalInfo,
} from '../../../../app-store/ProposalSlice'
import {resetJourney} from '../../../../app-store/AccountSetupSlice'
import {
  selectUserData,
  selectEffectiveRole,
} from '../../../../app-store/selector'
import {useAuthClient} from '../../../../utils/use-auth-client'
import {toast} from 'react-toastify'

export const ProposalCreationComponent = ({
  isProposalModalOpen,
  handleProposalModalOpenState,
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const {user} = useSelector(selectUserData)
  const effectiveRole = useSelector(selectEffectiveRole)
  const currentProposalInfo = useSelector(selectProposalInfo)

  useEffect(() => {
    if (isProposalModalOpen) {
      setCurrentStep(1)
      setLookupState('idle')
      setEmailLookupKey(k => k + 1)
      dispatch(resetJourney())
      dispatch(resetProposalInfo())
      dispatch(resetRecurlyAccountInfo())
    }
  }, [isProposalModalOpen, dispatch])
  const client = useAuthClient()
  const [lookupState, setLookupState] = useState('idle')
  const [currentStep, setCurrentStep] = useState(1)
  const [emailLookupKey, setEmailLookupKey] = useState(0)
  const [isFormValid, setIsFormValid] = useState(false)
  const [isSingleLocAcc, setSingleLocAccStatus] = useState(false)
  const handleEmailResponse = async data => {
    const outcome = data?.outcome ?? 'idle'
    setLookupState(outcome)

    const clientTypeMap = {
      'new-customer': 'NEW',
      'upgrade-customer': 'EXISTING',
    }
    dispatch(updateProposalInfo({clientType: clientTypeMap[outcome] ?? null}))

    let checkAccountType = {}
    if (data?.outcome !== 'idle') {
      if (data?.meta?.subDetails?.email) {
        checkAccountType = await client(
          `accounts/getAccounts?email=${data?.meta?.subDetails?.email}`,
        )
        if (checkAccountType.status === 'Success') {
          let isMultiLocationAcc =
            checkAccountType?.hierarchies?.[0]?.children?.length > 1
          if (isMultiLocationAcc) {
            toast.error(
              'Multi-location accounts are not supported at this time',
            )
          }
          setSingleLocAccStatus(!isMultiLocationAcc ?? false)
        }
      } else {
        setSingleLocAccStatus(true)
      }
    }
    dispatch(
      updateProposalInfo({
        clientEmail: data?.meta?.subDetails?.email ?? null,
        ...(outcome === 'upgrade-customer' && {
          thryvId: data?.meta?.thryvId ?? null,
        }),
      }),
    )
  }

  const handleValidityChange = useCallback(valid => {
    setIsFormValid(valid)
  }, [])

  const renderFooter = state => {
    if (!['new-customer', 'upgrade-customer'].includes(state)) return null
    if (!isSingleLocAcc) return null

    return (
      <FooterRow>
        {currentStep > 1 && (
          <Button
            variant="solid-gradiant"
            size="md"
            onClick={() => {
              setIsFormValid(false)
              setLookupState('idle')
              setCurrentStep(prev => prev - 1)
              dispatch(resetProposalInfo())
              dispatch(resetJourney())
            }}
          >
            {'Back'}
          </Button>
        )}
        <Button
          variant="solid-gradiant"
          size="md"
          disabled={currentStep > 1 && !isFormValid}
          onClick={() => {
            if (currentStep < 2) {
              setCurrentStep(prev => prev + 1)
            } else {
              // User confirmed — reset state now and carry forward the new
              // client details collected during the modal flow.
              const {
                clientEmail,
                clientType,
                thryvId,
                clientFirstName,
                clientLastName,
                clientPhone,
                businessName,
                country,
                salesRepOrPartner,
                salesRepInfo,
                xpCode,
                overrideSST,
                overrideRepInfo,
              } = currentProposalInfo
              dispatch(resetProposalInfo())
              dispatch(resetRecurlyAccountInfo())
              dispatch(
                updateProposalInfo({
                  salesRepId: user?.email ?? null,
                  userRole: effectiveRole?.[0] ?? null,
                  clientEmail,
                  clientType,
                  ...(thryvId && {thryvId}),
                  ...(clientFirstName && {clientFirstName}),
                  ...(clientLastName && {clientLastName}),
                  ...(clientPhone && {clientPhone}),
                  ...(businessName && {businessName}),
                  ...(country && {country}),
                  // The first-step attribution selection must survive this reset;
                  // useProposalSave persists these fields on the proposal record.
                  salesRepOrPartner,
                  salesRepInfo,
                  xpCode,
                  overrideSST,
                  overrideRepInfo,
                }),
              )
              handleProposalModalOpenState()
              navigate('/proposal-tool/proposal-builder')
            }
          }}
        >
          {currentStep === 1 ? 'Continue' : 'Create Proposal'}
        </Button>
      </FooterRow>
    )
  }
  const renderProposalModalContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <EmailLookup
            key={emailLookupKey}
            handleEmailResponse={handleEmailResponse}
            flowType={'proposal'}
          />
        )
      case 2:
        return (
          <ProposalAccountInformation
            isExistingAccount={lookupState === 'upgrade-customer'}
            accountInfo={{}}
            onValidityChange={handleValidityChange}
          />
        )
      default:
        return <></>
    }
  }
  return (
    <>
      {' '}
      <Modal
        isOpen={isProposalModalOpen}
        onClose={() => {
          handleProposalModalOpenState()
          setLookupState('idle')
          setCurrentStep(1)
          dispatch(resetJourney())
        }}
        closeOnBackdrop={false}
        closeOnEsc={false}
        title={'Create New Proposal'}
        subtitle={`Build a proposal with selected services`}
        size="lg"
        align={'center'}
        footer={renderFooter(lookupState)}
      >
        <div className="flex-column">{renderProposalModalContent()}</div>
      </Modal>
    </>
  )
}
